import { Box, Button, SvgIcon } from '@mui/material';
import Loading from 'components/common/Loading';
import deferRender from 'defer-render-hoc';
import FileSaver from 'file-saver';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import moment from 'moment';
import { object } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DEFAULT_CONFIG as DEFAULT_COMPLIANCE_CONFIG } from 'services/api/TimeTrackingCompliance';
import TimeTrackingCompliance from 'services/api/TimeTrackingCompliance';
import { TIME_TRACKING_APP_ID } from 'services/constants';
import companyShape from 'shapes/company';
import { useGetQuery } from 'store/app/entities/AppDataSlice';
import { I18n } from 'utils/i18n';
import connector from './connector';
import PeoplesTable from './PeoplesTable';
const SPREADSHEET_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

const rootSx = theme => ({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  backgroundColor: theme.palette.background.paper,
});

const loadingSx = {
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const monthSx = {
  width: 170,
  display: 'inline-block',
  textAlign: 'center',
  textTransform: 'capitalize',
};

const arrowIconSx = {
  fontSize: 24,
  color: 'primary.dark',
};

const loaderSx = {
  display: 'inline-block',
  marginLeft: '10px',
  width: 'auto',
};

const controlsSx = {
  display: 'flex',
  flex: 1,
};

const calendarSx = {
  alignItems: 'center',
  flex: 1,
  justifyContent: 'center',
  display: 'flex',
};

const TimeTrackingScene = ({
  company = null,
  timeTrackerLog: { loaded, loading, timeTrackerLog },
  actions,
}) => {
  const initialState = moment().startOf('month');

  const [month, setMonth] = useState(initialState);
  const [complianceConfig, setComplianceConfig] = useState(DEFAULT_COMPLIANCE_CONFIG);
  const [isGenerating, setIsGenerating] = useState(false);
  const [, setGenerateProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    actions.timeTrackerLog.loadMonth(month.toDate());
  }, [actions.timeTrackerLog, month]);

  const {
    data: complianceData,
    error: complianceError,
    isFetching: isFetchingCompliance,
    isLoading: isLoadingCompliance,
  } = useGetQuery({ appId: TIME_TRACKING_APP_ID }, { skip: !company?._id });

  useEffect(() => {
    if (!company?._id || isFetchingCompliance || isLoadingCompliance) {
      return;
    }

    const status =
      complianceError && typeof complianceError === 'object' && 'status' in complianceError
        ? complianceError.status
        : undefined;

    if (status && status !== 404) {
      setComplianceConfig(DEFAULT_COMPLIANCE_CONFIG);
      return;
    }

    setComplianceConfig(
      complianceData
        ? { ...DEFAULT_COMPLIANCE_CONFIG, ...complianceData }
        : DEFAULT_COMPLIANCE_CONFIG,
    );
  }, [company?._id, complianceData, complianceError, isFetchingCompliance, isLoadingCompliance]);

  const complianceEnabled = Boolean(complianceConfig.enabled && complianceConfig.complianceMode);

  const emp =
    timeTrackerLog?.map(({ employee, ...t }) => ({
      ...employee,
      timeTrackerLog: t,
    })) || [];

  const downloadReport = () => {
    if (complianceEnabled) {
      // In compliance mode, download from backend (server-side generation with audit logging)
      const url = `/api/v1/time-tracker-log/${month.year()}/${month.month() + 1}/export`;
      window.open(url, '_blank');
    } else {
      // In non-compliance mode, generate client-side as before
      import('xlsx').then(XLSX => {
        const data = [
          [
            'name',
            'code',
            'location',
            'email',
            'department',
            'jobTitle',
            'reportsTo',
            'tags',
            'notes',
            'status',
            'action',
            'timestamp',
            'expectedTime',
            'deviation',
          ],
        ];
        const fixedColumns = data[0].length;
        emp.forEach(e => {
          Object.values(e.timeTrackerLog.days).forEach(l => {
            l.shifts.forEach(s => {
              if (s.data && typeof s.data === 'object') {
                Object.keys(s.data).forEach(key => {
                  if (!data[0].includes(key)) {
                    data[0].push(key);
                  }
                });
              }
            });
          });
        });
        const additionalColumns = data[0].length - fixedColumns;
        emp.forEach(e => {
          Object.values(e.timeTrackerLog.days).forEach(l => {
            l.shifts.forEach(s => {
              const row = [
                e.name,
                e.code,

                e.location,
                e.email,
                e.department,
                e.jobTitle,
                e.reportsTo,
                e.tags,
                e.notes,
                e.status,
                s.type,
                s.date,
                s.data?.expectedTime,
                s.deviation,
                ...new Array(additionalColumns).fill(null),
              ];
              if (s.data && typeof s.data === 'object') {
                Object.entries(s.data).forEach(([key, value]) => {
                  const idx = data[0].findIndex(c => c === key);
                  row[idx] = value;
                });
              }
              data.push(row);
            });
          });
        });
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Logs');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const fileData = new Blob([excelBuffer], { type: SPREADSHEET_MIME_TYPE });
        FileSaver.saveAs(fileData, `${month.format('YYYY_MM')}_time_tracking_export.xlsx`);
      });
    }
  };

  const changeMonth = num => {
    setMonth(month.clone().add(num, 'month'));
  };

  const resetMonth = () => {
    setMonth(initialState);
  };

  const generateMonthlyReports = async () => {
    if (isGenerating || !emp.length) return;

    try {
      setIsGenerating(true);
      setGenerateProgress({ current: 0, total: emp.length });

      const monthKey = month.format('YYYY-MM');
      let successCount = 0;
      let errorCount = 0;

      // Generate PDFs for all employees sequentially
      for (let i = 0; i < emp.length; i++) {
        try {
          await TimeTrackingCompliance.regenerateReport(emp[i]._id, monthKey);
          successCount++;
        } catch (error) {
          console.error(`Failed to generate report for employee ${emp[i]._id}:`, error);
          errorCount++;
        }
        setGenerateProgress({ current: i + 1, total: emp.length });
      }

      // Show summary
      if (errorCount === 0) {
        alert(I18n.t('TimeTracking.GenerateSuccess', { count: successCount }));
      } else {
        alert(
          I18n.t('TimeTracking.GeneratePartialSuccess', {
            success: successCount,
            failed: errorCount,
          }),
        );
      }
    } catch (error) {
      console.error('Failed to generate monthly reports:', error);
      alert(I18n.t('TimeTracking.GenerateError'));
    } finally {
      setIsGenerating(false);
      setGenerateProgress({ current: 0, total: 0 });
    }
  };

  if (!company || !loaded) {
    return (
      <Box sx={loadingSx}>
        <Loading size={56} />
      </Box>
    );
  }

  const calendar = (
    <Box sx={controlsSx}>
      <Box sx={calendarSx}>
        <Button color="secondary" variant="outlined" onClick={resetMonth}>
          {I18n.t('TimeTracking.Current')}
        </Button>
        <Button onClick={() => changeMonth(-1)}>
          <SvgIcon sx={arrowIconSx}>
            <ChevronLeftIcon />
          </SvgIcon>
        </Button>
        <Box component="div" sx={monthSx}>
          {month.format('MMMM YYYY')}
          {loading && <Loading sx={loaderSx} size={12} />}
        </Box>
        <Button onClick={() => changeMonth(1)}>
          <SvgIcon sx={arrowIconSx}>
            <ChevronRightIcon />
          </SvgIcon>
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={rootSx}>
      <PeoplesTable
        company={company}
        employees={emp}
        additionalControls={calendar}
        onExport={downloadReport}
        complianceEnabled={complianceEnabled}
        onGenerateReports={generateMonthlyReports}
        isGenerating={isGenerating}
      />
      {!loading && <ReactTooltip id="time-tracking-day" />}
    </Box>
  );
};

TimeTrackingScene.propTypes = {
  company: companyShape,
  actions: object.isRequired,
  timeTrackerLog: object.isRequired,
};

export default deferRender(connector(TimeTrackingScene));
